import React, { useEffect } from "react";
import { Box, Typography, TextField, Button, IconButton, FormControl, Select, MenuItem, Alert } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useDispatch, useSelector } from "react-redux";
import { fetchContactProfileFields } from "../../redux/Slice/Settings/contactProfileFieldsSlice";
import { DEFAULT_SET_CONTACT_FIELDS_VALUE, SETTABLE_CORE_FIELD_OPTIONS, isAskSource } from "./setContactFieldsAction";

/**
 * Config UI for the "Set Contact Fields" action: pick fields, give each a value, and choose
 * whether to overwrite every selected field or only fill the ones that are empty.
 */
const SetContactFieldsEditor = ({ value, onChange, workspaceId, error, selectMenuProps, selectSx }) => {
  const dispatch = useDispatch();
  const profileFields = useSelector((state) => state.contactProfileFields?.items) || [];

  useEffect(() => {
    if (workspaceId) dispatch(fetchContactProfileFields(workspaceId));
  }, [workspaceId, dispatch]);

  const current = value ?? DEFAULT_SET_CONTACT_FIELDS_VALUE;
  const entries = Array.isArray(current.entries) ? current.entries : [];
  const fieldOptions = [...SETTABLE_CORE_FIELD_OPTIONS, ...profileFields];
  const update = (patch) => onChange({ ...current, ...patch });
  const updateEntry = (index, patch) => update({ entries: entries.map((e, i) => (i === index ? { ...e, ...patch } : e)) });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        Saves a value to each field below on the contact: either a fixed value, or the customer's answer to a question the workflow asks them. Fixed values can include variables like {"{{variable_name}}"}.
      </Typography>
      <FormControl size="small">
        <Select
          value={current.mode === "empty_only" ? "empty_only" : "overwrite"}
          onChange={(e) => update({ mode: e.target.value })}
          MenuProps={selectMenuProps}
          sx={selectSx}
        >
          <MenuItem value="overwrite">Overwrite all selected fields</MenuItem>
          <MenuItem value="empty_only">Only fill selected fields that are empty (won't ask for fields already filled)</MenuItem>
        </Select>
      </FormControl>

      {entries.map((entry, index) => {
        const takenElsewhere = new Set(entries.filter((_, i) => i !== index).map((e) => e.fieldId));
        const asking = isAskSource(entry);
        return (
          <Box key={index} sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={entry.fieldId || ""}
                  displayEmpty
                  onChange={(e) => updateEntry(index, { fieldId: e.target.value })}
                  MenuProps={selectMenuProps}
                  sx={selectSx}
                  renderValue={(v) => fieldOptions.find((f) => f._id === v)?.name || "Select field"}
                >
                  {fieldOptions.map((f) => (
                    <MenuItem key={f._id} value={f._id} disabled={takenElsewhere.has(f._id)}>{f.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={asking ? "ask" : "fixed"}
                  onChange={(e) => updateEntry(index, { source: e.target.value })}
                  MenuProps={selectMenuProps}
                  sx={selectSx}
                >
                  <MenuItem value="fixed">Fixed value</MenuItem>
                  <MenuItem value="ask">Ask the customer</MenuItem>
                </Select>
              </FormControl>
              <IconButton size="small" aria-label="Remove field" onClick={() => update({ entries: entries.filter((_, i) => i !== index) })}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            {asking ? (
              <TextField
                size="small"
                label="Question to ask"
                placeholder="Leave empty to use a default question"
                value={entry.question || ""}
                onChange={(e) => updateEntry(index, { question: e.target.value })}
                helperText="The workflow sends this, waits for the customer's reply, and saves the reply to the field."
              />
            ) : (
              <TextField
                size="small"
                label="Value"
                value={entry.value || ""}
                onChange={(e) => updateEntry(index, { value: e.target.value })}
              />
            )}
          </Box>
        );
      })}

      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => update({ entries: [...entries, { fieldId: "", source: "fixed", value: "", question: "" }] })}
        sx={{ alignSelf: "flex-start", textTransform: "none" }}
      >
        Add field
      </Button>
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
};

export default SetContactFieldsEditor;
